/**
 * Trading Bot Backend API Service
 *
 * Centralized API calls for Autopilot Trading Bot Backend
 * Base URL: http://localhost:3001 (development)
 * All endpoints require JWT authentication (except auth routes)
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import type {
  Strategy,
  TradingPosition,
  PortfolioSnapshot,
  BacktestConfig,
  BacktestResult,
  StrategyType,
} from '../types';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
});

// Request interceptor - Add JWT token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('tradingBotAuthToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors globally
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response) {
      const status = error.response.status;
      switch (status) {
        case 401:
          localStorage.removeItem('tradingBotAuthToken');
          console.error('Unauthorized - Please login');
          break;
        case 403:
          console.error('Forbidden - Insufficient permissions');
          break;
        case 404:
          console.error('Resource not found');
          break;
        case 500:
          console.error('Server error - Please try again later');
          break;
        default:
          console.error(`API Error: ${status}`);
      }
    } else if (error.request) {
      console.error('Network error - Backend may be offline');
    } else {
      console.error('Request error:', error.message);
    }
    return Promise.reject(error);
  }
);

// API Response Types
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    message: string;
    code?: string;
    details?: any;
  };
  meta?: {
    page?: number;
    limit?: number;
    total?: number;
  };
}

// Portfolio API
export const portfolioAPI = {
  getPortfolio: async (): Promise<ApiResponse<PortfolioSnapshot>> => {
    const response = await api.get('/api/portfolio');
    return response.data;
  },

  getHistory: async (days: number = 30): Promise<ApiResponse<any[]>> => {
    const response = await api.get(`/api/portfolio/history?days=${days}`);
    return response.data;
  },

  getAllocation: async (): Promise<ApiResponse<any>> => {
    const response = await api.get('/api/portfolio/allocation');
    return response.data;
  },
};

// Strategy API
export const strategyAPI = {
  getAll: async (filters?: { type?: StrategyType; status?: string }): Promise<ApiResponse<Strategy[]>> => {
    const params = new URLSearchParams();
    if (filters?.type) params.append('type', filters.type);
    if (filters?.status) params.append('status', filters.status);
    const response = await api.get(`/api/strategies?${params.toString()}`);
    return response.data;
  },

  getById: async (id: string): Promise<ApiResponse<Strategy>> => {
    const response = await api.get(`/api/strategies/${id}`);
    return response.data;
  },

  create: async (data: { name: string; type: StrategyType; config: any; description?: string }): Promise<ApiResponse<Strategy>> => {
    const response = await api.post('/api/strategies', data);
    return response.data;
  },

  update: async (id: string, data: Partial<Strategy>): Promise<ApiResponse<Strategy>> => {
    const response = await api.put(`/api/strategies/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<ApiResponse<void>> => {
    const response = await api.delete(`/api/strategies/${id}`);
    return response.data;
  },

  activate: async (id: string): Promise<ApiResponse<Strategy>> => {
    const response = await api.post(`/api/strategies/${id}/activate`);
    return response.data;
  },

  pause: async (id: string): Promise<ApiResponse<Strategy>> => {
    const response = await api.post(`/api/strategies/${id}/pause`);
    return response.data;
  },

  stop: async (id: string): Promise<ApiResponse<Strategy>> => {
    const response = await api.post(`/api/strategies/${id}/stop`);
    return response.data;
  },
};

// Position API
export const positionAPI = {
  getAll: async (filters?: { status?: string; strategyId?: string }): Promise<ApiResponse<TradingPosition[]>> => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.strategyId) params.append('strategyId', filters.strategyId);
    const response = await api.get(`/api/positions?${params.toString()}`);
    return response.data;
  },

  getById: async (id: string): Promise<ApiResponse<TradingPosition>> => {
    const response = await api.get(`/api/positions/${id}`);
    return response.data;
  },
};

// Performance API
export const performanceAPI = {
  getMetrics: async (strategyId: string): Promise<ApiResponse<any>> => {
    const response = await api.get(`/api/performance/${strategyId}`);
    return response.data;
  },

  getHistory: async (strategyId: string, days: number = 30): Promise<ApiResponse<any[]>> => {
    const response = await api.get(`/api/performance/${strategyId}/history?days=${days}`);
    return response.data;
  },
};

// Backtest API
export const backtestAPI = {
  run: async (config: BacktestConfig): Promise<ApiResponse<{ id: string; status: string }>> => {
    const response = await api.post('/api/backtest', config);
    return response.data;
  },

  getAll: async (filters?: { status?: string; limit?: number; offset?: number }): Promise<ApiResponse<BacktestResult[]>> => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());
    const response = await api.get(`/api/backtest?${params.toString()}`);
    return response.data;
  },

  getById: async (id: string): Promise<ApiResponse<BacktestResult>> => {
    const response = await api.get(`/api/backtest/${id}`);
    return response.data;
  },

  delete: async (id: string): Promise<ApiResponse<void>> => {
    const response = await api.delete(`/api/backtest/${id}`);
    return response.data;
  },
};

// Auth API
export const authAPI = {
  login: async (email: string, password: string): Promise<ApiResponse<{ token: string; user: any }>> => {
    const response = await api.post('/api/auth/login', { email, password });
    if (response.data.success && response.data.data?.token) {
      localStorage.setItem('tradingBotAuthToken', response.data.data.token);
    }
    return response.data;
  },

  register: async (email: string, password: string, name?: string): Promise<ApiResponse<{ token: string; user: any }>> => {
    const response = await api.post('/api/auth/register', { email, password, name });
    if (response.data.success && response.data.data?.token) {
      localStorage.setItem('tradingBotAuthToken', response.data.data.token);
    }
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('tradingBotAuthToken');
  },
};

// Health Check
export const healthAPI = {
  check: async (): Promise<ApiResponse<any>> => {
    const response = await api.get('/health');
    return response.data;
  },
};

// Exchange Connection API
export const exchangeAPI = {
  test: async (apiKey: string, apiSecret: string, testnet: boolean = true): Promise<ApiResponse<{ connected: boolean; testnet: boolean; accountInfo: any }>> => {
    const response = await api.post('/api/exchange/test', { apiKey, apiSecret, testnet });
    return response.data;
  },

  connect: async (apiKey: string, apiSecret: string, testnet: boolean = true, label?: string): Promise<ApiResponse<{ id: string; exchange: string; label: string; testnet: boolean; isActive: boolean; connectedAt: Date }>> => {
    const response = await api.post('/api/exchange/connect', { apiKey, apiSecret, testnet, label });
    return response.data;
  },

  getStatus: async (): Promise<ApiResponse<{ connected: boolean; exchange?: string; label?: string; testnet?: boolean; connectedAt?: Date; lastTestedAt?: Date }>> => {
    const response = await api.get('/api/exchange/status');
    return response.data;
  },

  disconnect: async (): Promise<ApiResponse<{ message: string }>> => {
    const response = await api.delete('/api/exchange/disconnect');
    return response.data;
  },
};

export default api;
